export {Network as Page};

import {useEffect, useState} from "preact/hooks";
import messages from "../../messages/messages.js";
import API_URL, {withSite} from "../../includes/api.js";
import {Helmet} from "react-helmet-async";
import {ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon} from "@heroicons/react/24/solid";
import {LazyImage} from "../../components/LazyImage.jsx";
import {usePageContext} from "../../hooks/usePageContext.js";

function Network() {

    // Seeded from +data.shared.js's server-side fetch so sliders and stats are
    // populated before the client-side fetches below complete after hydration.
    const seededData = usePageContext()?.data;
    const hasSeededData = Boolean(seededData?.networkData);

    const [showDialog, setShowDialog] = useState(false);
    const [dialogContent, setDialogContent] = useState({});
    const [networkData, setNetworkData] = useState(seededData?.networkData ?? []);
    const [subscribers, setSubscribers] = useState(seededData?.subscribers ?? []);
    const [isSwiperReady, setIsSwiperReady] = useState(false);

    useEffect(() => {
        if (!hasSeededData) {
            const endpoints = [
                {url: "/member/network", setState: setNetworkData},
                {url: "/subscriber/getsubscriberssite", setState: setSubscribers},
            ];

            endpoints.forEach(({url, setState}) => {
                fetch(withSite(API_URL + url))
                    .then(response => response.json())
                    .then(data => {
                        setState(data.code === 200 ? data.data : []);
                    });
            });
        }

        let unregister;
        let isMounted = true;
        const loadSwiper = async () => {
            const {register} = await import("swiper/element");

            unregister = register();
            await customElements.whenDefined('swiper-container');

            if (isMounted) {
                setIsSwiperReady(true);
            }
        };

        loadSwiper();

        // Clean up if you need to unregister
        return () => {
            isMounted = false;
            if (unregister) {
                unregister();
            }
        };

    }, []);

    // Initialize swipers when network data is loaded
    useEffect(() => {
        if (!isSwiperReady) {
            return;
        }

        if (!networkData?.members?.length && !networkData?.pioneers?.length && !networkData?.students?.length && !networkData?.testimonials?.length) {
            return;
        }

        // Delay to ensure DOM is fully rendered with slide elements
        const timeout = setTimeout(async () => {
            const {Navigation, Pagination, Autoplay} = await import("swiper/modules");

            initializeSwiper('.swiper-network-members', {
                modules: [Navigation, Autoplay],
                slidesPerView: 12,
                autoplay: {delay: 2000},
                navigation: true,
                breakpoints: {
                    0: {slidesPerView: 3, spaceBetween: 10},
                    390: {slidesPerView: 4, spaceBetween: 10},
                    640: {slidesPerView: 5},
                    768: {slidesPerView: 7},
                    992: {slidesPerView: 9},
                    1200: {slidesPerView: 10},
                    1400: {slidesPerView: 12}
                }
            });

            initializeSwiper('.swiper-pioneers', {
                modules: [Navigation, Autoplay],
                slidesPerView: 12,
                autoplay: {delay: 2500},
                navigation: true,
                breakpoints: {
                    0: {slidesPerView: 3, spaceBetween: 10},
                    390: {slidesPerView: 4, spaceBetween: 10},
                    640: {slidesPerView: 5},
                    768: {slidesPerView: 7},
                    992: {slidesPerView: 9},
                    1200: {slidesPerView: 10},
                    1400: {slidesPerView: 12}
                }
            });

            initializeSwiper('.swiper-students', {
                modules: [Navigation, Autoplay],
                slidesPerView: 12,
                autoplay: {delay: 2500},
                navigation: true,
                breakpoints: {
                    0: {slidesPerView: 3, spaceBetween: 10},
                    390: {slidesPerView: 4, spaceBetween: 10},
                    640: {slidesPerView: 5},
                    768: {slidesPerView: 7},
                    992: {slidesPerView: 9},
                    1200: {slidesPerView: 10},
                    1400: {slidesPerView: 12}
                }
            });

            initializeSwiper('.swiper-testimonials', {
                modules: [Navigation, Pagination, Autoplay],
                slidesPerView: 3,
                slidesPerGroup: 2,
                pagination: {el: ".swiper-pagination-test"},
                autoplay: {delay: 5000},
                breakpoints: {
                    0: {slidesPerView: 1, spaceBetween: 10, slidesPerGroup: 1},
                    640: {slidesPerView: 2, spaceBetween: 20, slidesPerGroup: 2},
                    1000: {slidesPerView: 3, spaceBetween: 30, slidesPerGroup: 3}
                }
            });
        }, 100);

        return () => clearTimeout(timeout);
    }, [isSwiperReady, networkData])

    // when clicked outside of dialog, close it
    useEffect(() => {
        const closeDialog = (e) => {
            if (e.target.id === 'member-dialog' && showDialog) {
                setShowDialog(false);
            }
        }
        window.addEventListener('click', closeDialog);
        return () => window.removeEventListener('click', closeDialog);
    }, [showDialog]);

    const changeMember = (prev) => () => {
        const index = networkData?.members?.findIndex(member => member.channel_id === dialogContent.channel_id);
        const newIndex = (prev ? (index - 1 + networkData?.members?.length) : (index + 1)) % networkData?.members?.length;
        setDialogContent(networkData?.members[newIndex]);
    };

    const initializeSwiper = (selector, params) => {
        const swiperEl = document.querySelector(selector);

        if (!swiperEl || typeof swiperEl.initialize !== 'function') {
            return;
        }

        params.loop = true;
        params.speed = 500;
        params.spaceBetween = 20;

        try {
            Object.assign(swiperEl, params);
            swiperEl.initialize();

            // only center when the slides don't fill the row, otherwise the overflow shifts them off-screen
            const {slides, params: {slidesPerView}} = swiperEl.swiper;
            if (slides.length < slidesPerView) {
                const wrapper = swiperEl.shadowRoot?.querySelector('.swiper-wrapper');

                if (wrapper) {
                    wrapper.style.justifyContent = 'center';
                }
            }
        } finally {
            swiperEl.classList.remove('swiper-pre-init');
        }
    }

    return (<>
        <section className="text-white">

            <Helmet>
                <title>{messages["META.NETWORK.TITLE"]}</title>
                <meta name="description" content={messages["META.NETWORK.DESC"]}/>
            </Helmet>

            <div id="member-dialog"
                 className={`${showDialog ? 'block' : 'hidden'} fixed top-0 left-0 right-0 z-50 p-4 overflow-x-hidden overflow-y-auto md:inset-0 h-full bg-black/30`}
                 role="dialog"
                 aria-modal="true"
                 aria-labelledby="network-member-dialog-title"
                 style={{height: "100%"}}>
                <div className="relative w-full max-w-md max-h-full mx-auto top-40">
                    <button
                        className="text-white swiper-button-prev absolute cursor-pointer bottom-0 top-0 flex items-center"
                        aria-label="Previous member"
                        onClick={changeMember(true)}>
                        <ChevronLeftIcon className="w-5 h-5" aria-hidden="true"/>
                    </button>
                    <button
                        className="text-white swiper-button-next absolute cursor-pointer bottom-0 top-0 flex items-center"
                        aria-label="Next member"
                        onClick={changeMember(false)}>
                        <ChevronRightIcon className="w-5 h-5" aria-hidden="true"/>
                    </button>
                    <div
                        className="relative bg-neutral-900 rounded-lg shadow-sm border-2 border-white p-6 text-center flex flex-row gap-5 gap-y-3 flex-wrap">
                        <button onClick={() => setShowDialog(false)} aria-label="Close member dialog"
                                className="absolute top-3 right-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center">
                            <XMarkIcon className="w-5 h-5" aria-hidden="true"/>
                        </button>
                        <img alt={dialogContent.channel_name} src={dialogContent.image_data}
                             loading="lazy"
                             decoding="async"
                             onError={(e) => e.target.src = '/img/Portrait_Placeholder.png'}
                             className="rounded-lg h-full" style={{maxWidth: 100}}/>
                        <div className="flex flex-col justify-center items-start gap-2">
                            <h2 id="network-member-dialog-title" className="text-xl font-bold m-0">{dialogContent.channel_name}</h2>
                            <a href={`https://youtube.com/channel/` + dialogContent.channel_id} target="_blank" rel="noopener noreferrer"
                               className="inline-block text-sm py-2 px-5 font-medium text-center text-white rounded-lg border border-gray-300 hover:bg-white hover:text-black">Visit
                                channel</a>
                        </div>
                    </div>
                </div>
            </div>

            <div className="text-center">

                <img alt="Network banner image" className="rounded-lg mb-10 object-cover md:object-contain w-full h-[120px] md:h-[320px]"
                     loading="eager"
                     fetchPriority="high"
                     src={`/img/${messages["NETWORK.IMAGE"]}`}/>

                <h1>Network Members</h1>
                <p>
                    The {messages["SITE.NAME"]} community currently has <b
                    className="text-red-400">{parseInt(networkData?.stats?.members).toLocaleString()}</b> members!<br/>
                    These channels combined have a total of <b
                    className="text-red-400">{parseInt(networkData?.stats?.subs).toLocaleString()}</b> subscribers
                    and <b className="text-red-400">{parseInt(networkData?.stats?.views).toLocaleString()}</b> views!
                </p>

                <swiper-container init="false"
                                  className="mb-6 ease-linear! swiper-network swiper-network-members swiper-pre-init">
                    {networkData?.members?.map((member) => (<swiper-slide>
                        <LazyImage onClick={() => {
                            setDialogContent(member);
                            setShowDialog(true);
                        }} onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                setDialogContent(member);
                                setShowDialog(true);
                            }
                        }} alt={member.channel_name} src={member.image_data}
                             role="button" tabIndex={0}
                             className="rounded-lg cursor-pointer member"/>
                    </swiper-slide>))}
                </swiper-container>

                <div>
                    <a href="/get-in-touch?topic=apply-member"
                       className="inline-block py-2 px-6 font-medium text-center text-white rounded-full border border-gray-300 hover:bg-white hover:text-black cursor-pointer">Apply
                        now</a>

                    <a href="/members"
                       className="inline-flex justify-center items-center py-3 px-5 text-base text-center text-white rounded-lg">
                        All members
                        <ArrowRightIcon className="ml-2 w-4 h-4"/>
                    </a>
                </div>

            </div>
        </section>

        <section className="pt-2 text-white">
            <div className="text-center">

                <h2>Network Pioneers</h2>
                <p>A special thanks to our legacy members who helped get {messages['SITE.NAME']} off the ground!</p>

                <swiper-container init="false"
                                  className="mb-6 ease-linear! swiper-network swiper-pioneers swiper-pre-init">
                    {networkData?.pioneers?.map((member) => (<swiper-slide>
                        <LazyImage onClick={() => {
                            setDialogContent(member);
                            setShowDialog(true);
                        }} onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                setDialogContent(member);
                                setShowDialog(true);
                            }
                        }} alt={member.channel_name} src={member.image_data}
                             role="button" tabIndex={0}
                             className="rounded-lg cursor-pointer member"/>
                    </swiper-slide>))}
                </swiper-container>

                <div>
                    <a href="/get-in-touch?topic=apply-pioneer"
                       className="inline-block py-2 px-6 font-medium text-center text-white rounded-full border border-gray-300 hover:bg-white hover:text-black cursor-pointer">Apply
                        now</a>

                    <a href="/pioneers"
                       className="inline-flex justify-center items-center py-3 px-5 text-base text-center text-white rounded-lg">
                        All pioneers
                        <ArrowRightIcon className="ml-2 w-4 h-4"/>
                    </a>
                </div>
            </div>
        </section>

        {subscribers && subscribers.length > 0 && (
            <section className="pt-2 text-white">
                <div className="text-center">

                    <h2>Thank you to our supporters</h2>
                    <p>A shoutout to our subscribers who support our website! Take a look at their YouTube channels
                        linked below:</p>

                    <div className="flex flex-row gap-5 flex-wrap justify-center">
                        {subscribers.map((subscriber) => (
                            <a href={`https://` + subscriber.domain_name + '.' + messages['DOMAIN']} target="_blank" rel="noopener noreferrer"
                               className={"subscriber-container"}>
                                <LazyImage alt={subscriber.name} src={subscriber.profile_pic}
                                           className="rounded-lg subscriber w-28"/>
                            </a>
                        ))}
                    </div>

                    <a href='/subscribe' target="_blank" rel="noopener noreferrer"
                       className="inline-block py-2 px-6 mt-6 font-medium text-center text-white rounded-full border border-gray-300 hover:bg-white hover:text-black cursor-pointer">
                        Subscribe
                    </a>

                </div>
            </section>
        )}

        <section className="text-white">
            <div className="text-center">
                <div dangerouslySetInnerHTML={{__html: messages["NETWORK.WELCOME"]}}></div>

                <p className="text-center mb-10">If you have any questions about our network, do not hesitate to <a
                    href="/get-in-touch">get in touch</a>!</p>

                <swiper-container init="false"
                                  className="mb-5 ease-linear! swiper-testimonials text-left swiper-pre-init">
                    {networkData?.testimonials?.map((testimonial) => (
                        <swiper-slide
                            className="flex align-center flex-col grow items-start p-4 rounded-lg gap-4 border border-border/60 bg-zinc-800 cursor-pointer"
                            role="button"
                            tabIndex={0}
                            onClick={() => window.open(`https://youtube.com/channel/` + testimonial.channel_id, '_blank', 'noopener,noreferrer')}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    window.open(`https://youtube.com/channel/` + testimonial.channel_id, '_blank', 'noopener,noreferrer');
                                }
                            }}>
                            <div className="flex items-center gap-4">
                                <LazyImage alt={testimonial.channel_name} src={testimonial.image_data}
                                           class="rounded-lg"/>
                                <div class="flex flex-col items-start">
                                    <div class="font-bold text-xl">
                                        {testimonial.channel_name}
                                    </div>
                                    <div>{testimonial.sub_tag}</div>
                                </div>
                            </div>
                            <div class="italic text-base">"{testimonial.testimonial}"</div>
                        </swiper-slide>
                    ))}
                </swiper-container>

                <div className="swiper-pagination-test mb-5 flex items-center justify-center gap-1"></div>

                <a href="/get-in-touch?topic=apply-member"
                   className="inline-block py-2 px-6 font-medium text-center text-white rounded-full border border-gray-300 hover:bg-white hover:text-black cursor-pointer">Apply
                    now</a>

            </div>
        </section>

        <section className="text-white">
            <div className="text-center">

                <h2>{messages['SITE.NAME']} Member Benefits</h2>
                <p>The {messages['SITE.NAME']} network community offers a wide range of benefits to its
                    members.</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 text-left">
                    <div className="flex items-center flex-row gap-4">
                        <LazyImage className=" rounded-lg w-1/5" alt="Benefit icon"
                                   src={"/gifs/" + messages['NETWORK.BENEFIT.1.IMAGE']}/>
                        {messages['NETWORK.BENEFIT.1.TEXT']}
                    </div>
                    <div className="flex items-center flex-row gap-4">
                        <LazyImage className=" rounded-lg w-1/5" alt="Benefit icon"
                                   src={"/gifs/" + messages['NETWORK.BENEFIT.2.IMAGE']}/>
                        {messages['NETWORK.BENEFIT.2.TEXT']}
                    </div>
                    <div className="flex items-center flex-row gap-4">
                        <LazyImage className=" rounded-lg w-1/5" alt="Benefit icon"
                                   src={"/gifs/" + messages['NETWORK.BENEFIT.3.IMAGE']}/>
                        {messages['NETWORK.BENEFIT.3.TEXT']}
                    </div>
                    <div className="flex items-center flex-row gap-4">
                        <LazyImage className=" rounded-lg w-1/5" alt="Benefit icon"
                                   src={"/gifs/" + messages['NETWORK.BENEFIT.4.IMAGE']}/>
                        <span>Ambitious goals for your channel?
                            <br/><a target="_blank" rel="noopener noreferrer" href='/subscribe'>Subscribe
                                to {messages["DOMAIN"]}!</a>
                        </span>
                    </div>
                </div>

                <a href="/get-in-touch?topic=apply-member"
                   className="inline-block py-2 px-6 font-medium text-center text-white rounded-full border border-gray-300 hover:bg-white hover:text-black cursor-pointer">Apply
                    now</a>
            </div>
        </section>

        <section className="text-white">
            <div className="flex flex-col-reverse md:flex-row gap-4 md:gap-10 items-start">

                <div>
                    <h2>{messages['SITE.NAME']} Network Academy</h2>

                    <p>The {messages['SITE.NAME']} Network Academy is your gateway to growing as
                        a {messages['NAME']} creator and becoming part of a thriving, passionate
                        community—completely
                        free! Designed for creators with <b>fewer than 100 subscribers</b>, the Academy provides
                        mentorship,
                        constructive feedback, and resources to help you refine your skills, connect with like-minded
                        individuals, and expand your audience.</p>

                    <div className="border border-border/60 bg-zinc-800 px-5 py-4 mb-4 rounded-lg">
                        <h3 className="text-xl m-0">Benefits of Joining</h3>
                        <ul>
                            <li><b>A supportive community:</b> Collaborate with fellow creators and gain insights from
                                experienced mentors.
                            </li>
                            <li><b>Content feedback:</b> Share your work in dedicated channels for constructive input to
                                improve your videos.
                            </li>
                            <li><b>Growth resources:</b> Learn best practices for creating
                                captivating {messages['NAME']} content
                                and engaging your audience.
                            </li>
                        </ul>
                    </div>

                    <p>When you reach 100 subscribers and meet our quality standards, you’ll qualify for full membership
                        in the {messages['SITE.NAME']} Network, unlocking exclusive benefits like video features on our
                        website and advanced promotional opportunities.</p>

                    <p>Start your journey with the {messages['SITE.NAME']} Network Academy today—apply now for free and
                        take your {messages['NAME']} channel to the next level!</p>

                    <a href="/get-in-touch?topic=apply-academy"
                       className="inline-block py-2 px-6 font-medium text-center text-white rounded-full border border-gray-300 hover:bg-white hover:text-black cursor-pointer">Apply
                        now</a>
                </div>

                <LazyImage alt="Network academy banner image" className="rounded-lg md:mt-16"
                           src={"/img/academy-banner.png"}/>

            </div>
        </section>

        <section className="pt-2 text-white">
            <div className="text-center">

                <h2>Network Students</h2>
                <p>Helping students learn and grow their YouTube channels!</p>

                <swiper-container init="false"
                                  className="mb-6 ease-linear! swiper-network swiper-students swiper-pre-init">
                    {networkData?.students?.map((member) => (<swiper-slide>
                        <LazyImage onClick={() => {
                            setDialogContent(member);
                            setShowDialog(true);
                        }} onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                               setDialogContent(member);
                               setShowDialog(true);
                            }
                        }} alt={member.channel_name} src={member.image_data}
                             role="button" tabIndex={0}
                             className="rounded-lg cursor-pointer member"/>
                    </swiper-slide>))}
                </swiper-container>

                <div>
                    <a href="/get-in-touch?topic=apply-student"
                       className="inline-block py-2 px-6 font-medium text-center text-white rounded-full border border-gray-300 hover:bg-white hover:text-black cursor-pointer">Apply
                        now</a>
                </div>
            </div>
        </section>

        {messages["show_custom_designer"] === "true" && (
            <section className="text-white">
                <div className="text-center">
                    <div className="flex items-center flex-col gap-4 mx-auto text-center md:w-1/2">
                        <a href="https://mareeha.uwu.ai/" target="_blank" rel="noopener noreferrer">
                            <LazyImage className="rounded-lg" alt="Benefit icon" src={"/img/channels4_banner.jpg"}/>
                        </a>
                        <span>
                            Improve the look of your YouTube channel with custom designs created by Mareeha. <a
                            href='/subscribe'>Subscribers</a> receive this for free, or you can hire Mareeha for premium designs at <a
                            href="https://mareeha.uwu.ai/" target="_blank" rel="noopener noreferrer">mareeha.uwu.ai</a>
                        </span>
                    </div>
                </div>
            </section>
        )}
    </>);
}
